/**
 * Area `test-call` of the console gateway — owned by plan-002 unit **RB-42** (design §15.6.11).
 *
 * The Test console and the agent's Test tab. A voice test call is a real conversation on the live
 * VoiceAgent, billed like any other minute and marked `is_test` (AC-38); a text test is free.
 *
 * ── AMENDED 2026-09-25 (customer-portal review #91, #125, #164, #166) ─────────────────────────
 * The API now asks the agent_runtime, so every refusal here is a fact about THIS request, in the
 * API's own words: the model is resting outside its window (`503 model_pool_unavailable`), the
 * runtime did not answer or is restarting (`503 agent_runtime_unavailable`), every free line is
 * kept for inbound callers (`503 model_pool_unavailable`, "Agent calls me").
 * Both 503s become {@link TestCallUnavailableError}, so a screen shows them in a neutral panel — "not
 * possible right now", never a red failure and never a call that looks placed — with the API's
 * sentence. Every other refusal (`agent_version_not_ready`, `language_unavailable`,
 * `trial_exhausted`, "has no phone number yet", …) arrives as the generated client's `ApiError`.
 * `readTestCallAvailability` reads the same facts BEFORE anyone presses a button (review #166).
 *
 * ── THE TEXT CHAT ────────────────────────────────────────────────────────────────────────
 * The API stores a chat as a conversation and answers its `conversation_id`; the slice keeps each
 * agent's chat (its lines and that id) for the life of the gateway, sends the id back so the agent
 * sees the history, and returns the WHOLE chat so far. `startNewTestChat` forgets it; the next line
 * starts a new chat on the live version. A refused line adds nothing.
 *
 * ── THE VOICE TEST CALL ──────────────────────────────────────────────────────────────────
 * AMENDED 2026-09-25 (review 125): "Agent calls me" answers `dialing` — the tester's phone is ringing
 * from `dialNumber`, the agent's own number — and is read with the same `readVoiceTestCall`; a phone
 * that was not answered or was busy reads `ended` with `endReason` `no_answer` / `busy`.
 * `startVoiceTestCall` arms the tester's number ("I call the agent") and answers `idle` with the
 * number to dial and the waiting window; `readVoiceTestCall` reads how the call is going (idle →
 * dialing → live → ended). `languageCode ""` (the screen's "let the agent decide") is sent as
 * `null`, never as an empty code. No `Idempotency-Key` is sent: two deliberate tests are two tests.
 *
 * ── TALK IN THE BROWSER (plan-004 WC-51, design §17.10.1 IC-31, FR-BV-1…14) ──────────────────
 * `startBrowserTestCall` asks the api for a browser test on the live version: the test-call view in
 * `connecting`, the grant to join WeTalk's browser voice relay with (`webrtc`, camel-cased here into
 * IC-35's `BrowserVoiceGrant`), the limits, the list price and — when this start ended the member's
 * earlier browser test on this agent (FR-BV-13) — that conversation's id. `readBrowserTestCall` reads
 * it by conversation id (`connecting` → `live` → `ended` with what was charged, review #233); it is
 * ended with the same `endVoiceTestCall` as a phone test. Four 503s mean "not right now" and become
 * {@link TestCallUnavailableError}: the model resting, the runtime away, every line busy (AC-BV-6)
 * and the relay unreachable.
 *
 * ── "MY PHONE"'S WINDOW AND THE DOCUMENTS' NAMES (plan-015 TC1, G20, G23) ─────────────────────
 * `renewVoiceTestCallArm` keeps an armed number waiting (`renewTestCallArm`: the start unchanged, a new
 * `waitingUntil`; `test_call_arm_not_found` arrives as the client's `ApiError` and means "arm afresh"),
 * and `releaseVoiceTestCallArm` drops it when the panel closes (`releaseTestCallArm`, 204). The number
 * travels in the body. `listVoiceAgentLiveDocuments` reads the file names the live version answers from,
 * for the text hint. The three were optional until the sandbox gateway answered them too (plan-015 INT-A,
 * 2026-10-09); they are required.
 *
 * A release on `pagehide` should be sent with `fetch(…, { keepalive: true })` so it survives the page
 * going away (design §28.5.13); the generated client takes no per-request option, so this one is an
 * ordinary request and may be cut off by the browser — the arm then lapses at the end of its window.
 *
 * ── THE SURVEY'S QUESTION GROUP AND THE OPENING (plan-015 TC2, G21, G22, design §28.5.15) ─────────
 * A two-group survey's test names the group the tester chose — `surveyArm` `A` ("Test group 1") or `B`
 * ("group 2") — sent as `survey_arm` on every text turn of its chat, on the phone test
 * (`TestCallRequest.surveyArm`) and on the browser test. The api refuses a missing group for a two-group
 * survey and any group for one group or none. `openTestChat` opens a text chat of an agent that makes
 * calls (a survey) with its greeting — turn 0, before the tester types anything (`opening: true`); the
 * chat's later lines continue it. Required since INT-A, as the three above.
 *
 * `loadBrowserVoiceClient` is the ONLY way the console reaches `@wetalk/webrtc-client`: a dynamic
 * import, so neither that module nor the WebRTC SDK it loads in turn is in the console's entry
 * chunk (plan-004 R17). The sandbox gateway answers the same method with the module's scripted fake.
 */
import { ApiError, assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import type { Money } from "@wetalk/domain";
import type { BrowserVoiceGrant, connectBrowserVoice, requestMicrophone } from "@wetalk/webrtc-client";

import type {
  ConsoleGateway,
  TestCallAvailabilityView,
  TestCallDirection,
  TestCallRequest,
  TestCallState,
  TestCallView,
  TestChatTurnView,
  TranscriptLineView,
} from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber, MalformedResponseError } from "../decode.js";
import { readMoney, readNullableText } from "../conversation-view.js";
import { testCallVoiceAgentPassOfWire } from "../voice-agent-stint-view.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** A chat is kept per agent, and apart per version when a version is named (review #75). */
export function chatKeyOf(voiceAgentId: string, agentVersionId: string | undefined, languageCode = ""): string {
  return JSON.stringify([voiceAgentId, agentVersionId ?? null, languageCode]);
}
export interface TestChatSnapshot {
  readonly turn: readonly TestChatTurnView[];
  readonly ended: boolean;
  readonly pending?: string | null;
}

/**
 * The browser voice module (IC-35) as the console uses it: the two calls of `@wetalk/webrtc-client`,
 * typed from the module's own declarations (type-only, so nothing of it is bundled here).
 * `requestMicrophone` must be called inside the click that starts the test.
 */
export interface BrowserVoiceClient {
  readonly requestMicrophone: typeof requestMicrophone;
  readonly connectBrowserVoice: typeof connectBrowserVoice;
}

/** A browser test as the api started it (`createBrowserTestCall`'s 201). */
export interface BrowserTestCallView {
  /** The test-call view, `connecting`: nothing is billed until admission carries it. */
  readonly testCall: TestCallView;
  readonly languageCode: string;
  /** How to join WeTalk's browser voice relay, before `grant.joinBy`. */
  readonly grant: BrowserVoiceGrant;
  /** The longest the test may run (15 minutes). */
  readonly maxDurationSecond: number;
  /** How long before the end the countdown starts. */
  readonly finalMinuteSecond: number;
  /** The member's earlier browser test on this agent that this start ended (FR-BV-13), or `null`. */
  readonly replacedConversationId: string | null;
  /** What a minute of it is billed at (the account's list price). */
  readonly listPricePerMinute: Money;
}

/** An armed number's window after a renewal (plan-015 TC1). */
export interface TestCallArmWindowView {
  /** When the wait began; a renewal does not move it. */
  readonly waitingSince: string;
  /** When the arm lapses unless it is renewed again. */
  readonly waitingUntil: string;
}

/** A survey's question group: `A` is "Test group 1", `B` "group 2" (plan-015 TC2, design 9734–9735). */
export type SurveyArmView = "A" | "B";

/** One document the live version answers from (plan-015 TC1, G20). */
export interface VoiceAgentLiveDocumentView {
  readonly agentDocumentId: string;
  /** The person's own name for the file. */
  readonly filename: string;
}

declare module "../gateway.js" {
  interface TestCallRequest {
    /** plan-015 TC2 (G21): a two-group survey's question group; absent for anything else. */
    readonly surveyArm?: SurveyArmView;
  }
  interface ConsoleGateway {
    /**
     * plan-015 TC2 (G22): opens a NEW text chat of an agent that makes calls with its greeting — turn 0,
     * before any input — and returns the chat so far (that one line).
     */
    openTestChat(voiceAgentId: string, agentVersionId?: string, surveyArm?: SurveyArmView): Promise<readonly TestChatTurnView[]>;
    /**
     * plan-015 TC1 (G23): keeps "My phone"'s arm for `partyNumber` waiting another window. Raises the
     * client's `ApiError` `test_call_arm_not_found` when it lapsed or a call took it (arm afresh).
     */
    renewVoiceTestCallArm(voiceAgentId: string, partyNumber: string): Promise<TestCallArmWindowView>;
    /** plan-015 TC1 (G23): drops that arm (idempotent). */
    releaseVoiceTestCallArm(voiceAgentId: string, partyNumber: string): Promise<void>;
    /** plan-015 TC1 (G20): the file names the live version answers from. */
    listVoiceAgentLiveDocuments(voiceAgentId: string): Promise<readonly VoiceAgentLiveDocumentView[]>;
    /**
     * "Talk in the browser": arms a browser test on the live version (IC-31). `surveyArm` (plan-015 TC2):
     * a two-group survey's question group.
     */
    startBrowserTestCall(voiceAgentId: string, languageCode: string, surveyArm?: SurveyArmView): Promise<BrowserTestCallView>;
    /** How a browser test is going, by its conversation id. */
    readBrowserTestCall(voiceAgentId: string, conversationId: string): Promise<TestCallView>;
    /** The browser voice module, loaded on demand (never in the entry chunk). */
    loadBrowserVoiceClient(): Promise<BrowserVoiceClient>;
    startVoiceTestCall(request: TestCallRequest): Promise<TestCallView>;
    /** How the armed "I call the agent" test is going, since `waitingSince`. */
    readVoiceTestCall(voiceAgentId: string, partyNumber: string, waitingSince: string): Promise<TestCallView>;
    endVoiceTestCall(conversationId: string): Promise<TestCallView>;
    /**
     * Free. Text chat is not metered. Returns the whole chat with this agent so far. With
     * `agentVersionId` the chat is on that built version rather than the live one — an edit tried
     * before it is published (customer-portal review #75) — and is kept apart from the live chat.
     * `surveyArm` (plan-015 TC2): a two-group survey's question group, sent with every line of the chat.
     */
    sendTestChatTurn(
      voiceAgentId: string,
      text: string,
      languageCode?: string,
      agentVersionId?: string,
      surveyArm?: SurveyArmView,
    ): Promise<readonly TestChatTurnView[]>;
    /** Forgets this agent's chat (on that version); the next line starts a new one. */
    startNewTestChat(voiceAgentId: string, agentVersionId?: string, languageCode?: string): Promise<void>;
    /** Server-confirmed lines retained while the panel is closed. */
    testChatSnapshot(voiceAgentId: string, agentVersionId?: string, languageCode?: string): TestChatSnapshot;
    subscribeTestChat(listener: () => void): () => void;
    /** What can be tested right now, and why not (review #166). */
    readTestCallAvailability(): Promise<TestCallAvailabilityView>;
  }
}

/** Empty: every operation of the area is in the spec. */
export const testCallGap: readonly GatewayGap[] = [];

/** The operation that could not run. */
export type TestCallOperation =
  | "createTestCall"
  | "createBrowserTestCall"
  | "readTestCall"
  | "endTestCall"
  | "createSandboxTurn"
  | "readTestCallAvailability"
  | "renewTestCallArm"
  | "releaseTestCallArm";

/**
 * The API answered `503`: the test could not run right now — the language model is resting
 * (`model_pool_unavailable`; it runs in scheduled windows), the runtime did not answer, or the
 * runtime is restarting (`agent_runtime_unavailable`); for a browser test also every one of the
 * agent's lines is busy (`all_lines_busy`, AC-BV-6) or WeTalk's browser voice relay did not answer
 * (`webrtc_unavailable`). Nothing was placed, ended or answered, and nothing was charged.
 *
 * Its own class so a screen can tell "not possible right now" from "that failed", and so no screen
 * can mistake the refusal for a result. `message` is the API's sentence, written for a person;
 * `requestId` is for support.
 */
export class TestCallUnavailableError extends Error {
  override readonly name = "TestCallUnavailableError";
  readonly operation: TestCallOperation;
  readonly code: TestCallUnavailableCode;
  readonly requestId: string | null;
  constructor(operation: TestCallOperation, cause: ApiError, code: TestCallUnavailableError["code"]) {
    super(cause.message, { cause });
    this.operation = operation;
    this.code = code;
    this.requestId = cause.requestId;
  }
}

const STATE: readonly TestCallState[] = ["idle", "dialing", "connecting", "live", "ended"];
const SPEAKER: readonly TranscriptLineView["speaker"][] = ["assistant", "caller", "system"];
const DIRECTION: readonly TestCallDirection[] = ["agent_calls_me", "i_call_the_agent"];

const UNAVAILABLE_CODE = [
  "model_pool_unavailable",
  "agent_runtime_unavailable",
  "all_lines_busy",
  "webrtc_unavailable",
] as const;

/** The 503 codes that mean "not right now" rather than "that failed". */
export type TestCallUnavailableCode = (typeof UNAVAILABLE_CODE)[number];

function unavailableCodeOf(code: string): TestCallUnavailableCode | null {
  return UNAVAILABLE_CODE.find((one) => one === code) ?? null;
}

/** `assertOk`, with the refusals that mean "not right now" turned into their own class. */
function bodyOf(result: ApiResult, operation: TestCallOperation): unknown {
  try {
    return assertOk(result);
  } catch (raised) {
    if (raised instanceof ApiError) {
      const unavailable = unavailableCodeOf(raised.code);
      if (unavailable !== null) throw new TestCallUnavailableError(operation, raised, unavailable);
    }
    throw raised;
  }
}

function testCallOf(body: unknown, operation: TestCallOperation): TestCallView {
  return testCallOfData(readRecord(readRecord(body, operation)["data"], `${operation}.data`), operation);
}

function testCallOfData(data: Readonly<Record<string, unknown>>, operation: TestCallOperation): TestCallView {
  const state = readText(data["state"], `${operation}.state`);
  const knownState = STATE.find((one) => one === state);
  if (knownState === undefined) throw new MalformedResponseError(`${operation}.state`, "a test-call state", state);
  const optionalText = (field: string): string | null =>
    data[field] === undefined ? null : readNullableText(data[field], `${operation}.${field}`);
  return {
    conversationId: readNullableText(data["conversation_id"], `${operation}.conversation_id`),
    state: knownState,
    elapsedSeconds: readWholeNumber(data["elapsed_second"], `${operation}.elapsed_second`),
    billedSeconds: readWholeNumber(data["billed_second"], `${operation}.billed_second`),
    billedAmount: readMoney(data["billed_amount"], `${operation}.billed_amount`),
    chargedSeconds:
      data["charged_second"] === null ? null : readWholeNumber(data["charged_second"], `${operation}.charged_second`),
    chargedAmount:
      data["charged_amount"] === null ? null : readMoney(data["charged_amount"], `${operation}.charged_amount`),
    transcript: readArray(data["transcript"], `${operation}.transcript`).map((wire, index) => {
      const path = `${operation}.transcript[${index}]`;
      const line = readRecord(wire, path);
      const speaker = readText(line["speaker"], `${path}.speaker`);
      const knownSpeaker = SPEAKER.find((one) => one === speaker);
      if (knownSpeaker === undefined) throw new MalformedResponseError(`${path}.speaker`, "a speaker", speaker);
      return {
        speaker: knownSpeaker,
        text: readText(line["text"], `${path}.text`),
        partial: readBoolean(line["partial"], `${path}.partial`),
        // plan-021: the stint a stored line belongs to (on every stored line; a revising one carries none).
        ...(line["voice_agent_stint_ordinal"] === undefined
          ? {}
          : { voiceAgentStintOrdinal: readWholeNumber(line["voice_agent_stint_ordinal"], `${path}.voice_agent_stint_ordinal`) }),
      };
    }),
    // plan-021 (design §33.13.3, OD-17): the VoiceAgents that answered the test and its pass events.
    ...testCallVoiceAgentPassOfWire(data, operation),
    endReason: readNullableText(data["end_reason"], `${operation}.end_reason`),
    dialNumber: optionalText("dial_number"),
    waitingSince: optionalText("waiting_since"),
    waitingUntil: optionalText("waiting_until"),
  };
}

function browserTestCallOf(body: unknown): BrowserTestCallView {
  const operation = "createBrowserTestCall";
  const data = readRecord(readRecord(body, operation)["data"], `${operation}.data`);
  const webrtc = readRecord(data["webrtc"], `${operation}.webrtc`);
  return {
    testCall: testCallOfData(data, operation),
    languageCode: readText(data["language_code"], `${operation}.language_code`),
    grant: {
      serverUrl: readText(webrtc["server_url"], `${operation}.webrtc.server_url`),
      joinToken: readText(webrtc["join_token"], `${operation}.webrtc.join_token`),
      joinBy: readText(webrtc["join_by"], `${operation}.webrtc.join_by`),
      bridgeIdentity: readText(webrtc["bridge_identity"], `${operation}.webrtc.bridge_identity`),
      participantIdentity: readText(webrtc["participant_identity"], `${operation}.webrtc.participant_identity`),
    },
    maxDurationSecond: readWholeNumber(data["max_duration_second"], `${operation}.max_duration_second`),
    finalMinuteSecond: readWholeNumber(data["final_minute_second"], `${operation}.final_minute_second`),
    replacedConversationId: readNullableText(data["replaced_conversation_id"], `${operation}.replaced_conversation_id`),
    listPricePerMinute: readMoney(data["list_price_per_minute"], `${operation}.list_price_per_minute`),
  };
}

interface SandboxTurnAnswer {
  readonly ended: boolean;
  readonly conversationId: string;
  readonly turn: readonly TestChatTurnView[];
}

function sandboxTurnOf(body: unknown): SandboxTurnAnswer {
  const data = readRecord(readRecord(body, "createSandboxTurn")["data"], "createSandboxTurn.data");
  return {
    conversationId: readText(data["conversation_id"], "createSandboxTurn.conversation_id"),
    ended: data["ended"] === undefined ? false : readBoolean(data["ended"], "createSandboxTurn.ended"),
    turn: readArray(data["turn"], "createSandboxTurn.turn").map((wire, index) => {
      const path = `createSandboxTurn.turn[${String(index)}]`;
      const line = readRecord(wire, path);
      const speaker = readText(line["speaker"], `${path}.speaker`);
      if (speaker !== "assistant" && speaker !== "caller" && speaker !== "system") {
        throw new MalformedResponseError(`${path}.speaker`, "assistant, caller or system", speaker);
      }
      return { speaker, text: readText(line["text"], `${path}.text`) };
    }),
  };
}

function armWindowOf(body: unknown): TestCallArmWindowView {
  const data = readRecord(readRecord(body, "renewTestCallArm")["data"], "renewTestCallArm.data");
  return {
    waitingSince: readText(data["waiting_since"], "renewTestCallArm.waiting_since"),
    waitingUntil: readText(data["waiting_until"], "renewTestCallArm.waiting_until"),
  };
}

function liveDocumentOf(body: unknown): readonly VoiceAgentLiveDocumentView[] {
  const operation = "listVoiceAgentLiveDocuments";
  return readArray(readRecord(body, operation)["data"], `${operation}.data`).map((wire, index) => {
    const path = `${operation}.data[${index}]`;
    const one = readRecord(wire, path);
    return {
      agentDocumentId: readText(one["agent_document_id"], `${path}.agent_document_id`),
      filename: readText(one["filename"], `${path}.filename`),
    };
  });
}

function availabilityOf(body: unknown): TestCallAvailabilityView {
  const data = readRecord(readRecord(body, "readTestCallAvailability")["data"], "readTestCallAvailability.data");
  return {
    modelRunning: readBoolean(data["model_running"], "readTestCallAvailability.model_running"),
    sandboxTurnAvailable: readBoolean(data["sandbox_turn_available"], "readTestCallAvailability.sandbox_turn_available"),
    directionAvailable: readArray(data["direction_available"], "readTestCallAvailability.direction_available").map(
      (wire, index) => {
        const path = `readTestCallAvailability.direction_available[${String(index)}]`;
        const direction = readText(wire, path);
        const known = DIRECTION.find((one) => one === direction);
        if (known === undefined) throw new MalformedResponseError(path, "a test-call direction", direction);
        return known;
      },
    ),
    message: readNullableText(data["message"], "readTestCallAvailability.message"),
    browserTestCallAvailable: readBoolean(
      data["browser_test_call_available"],
      "readTestCallAvailability.browser_test_call_available",
    ),
    browserTestCallMessage: readNullableText(
      data["browser_test_call_message"],
      "readTestCallAvailability.browser_test_call_message",
    ),
    countryCallingCode: readNullableText(data["country_calling_code"], "readTestCallAvailability.country_calling_code"),
  };
}

export function testCallApiSlice(
  client: WeTalkApiClient,
  _context: SliceContext,
): Pick<
  ConsoleGateway,
  | "startVoiceTestCall"
  | "readVoiceTestCall"
  | "endVoiceTestCall"
  | "sendTestChatTurn"
  | "startNewTestChat"
  | "testChatSnapshot"
  | "subscribeTestChat"
  | "readTestCallAvailability"
  | "startBrowserTestCall"
  | "readBrowserTestCall"
  | "loadBrowserVoiceClient"
  | "renewVoiceTestCallArm"
  | "releaseVoiceTestCallArm"
  | "listVoiceAgentLiveDocuments"
  | "openTestChat"
> {
  /** Each agent's text chat so far, and the conversation that holds it, for this gateway's lifetime. */
  const chat = new Map<string, TestChatSnapshot & { readonly conversationId: string }>();
  const listener = new Set<() => void>();
  const changed = (): void => { for (const notify of listener) notify(); };
  const generation = new Map<string, number>();
  const attempt = new Map<string, { readonly text: string; readonly requestId: string; running: boolean }>();

  return {
    async startVoiceTestCall(request: TestCallRequest): Promise<TestCallView> {
      const result = await client.createTestCall({
        path: { voice_agent_id: request.voiceAgentId },
        body: {
          direction: request.direction,
          party_number: request.partyNumber,
          language_code: request.languageCode === "" ? null : request.languageCode,
          ...(request.surveyArm === undefined ? {} : { survey_arm: request.surveyArm }),
        },
      });
      return testCallOf(bodyOf(result, "createTestCall"), "createTestCall");
    },

    async readVoiceTestCall(voiceAgentId: string, partyNumber: string, waitingSince: string): Promise<TestCallView> {
      const result = await client.readTestCall({
        path: { voice_agent_id: voiceAgentId },
        query: { party_number: partyNumber, since: waitingSince },
      });
      return testCallOf(bodyOf(result, "readTestCall"), "readTestCall");
    },

    async renewVoiceTestCallArm(voiceAgentId: string, partyNumber: string): Promise<TestCallArmWindowView> {
      const result = await client.renewTestCallArm({ path: { voice_agent_id: voiceAgentId }, body: { party_number: partyNumber } });
      return armWindowOf(bodyOf(result, "renewTestCallArm"));
    },

    async releaseVoiceTestCallArm(voiceAgentId: string, partyNumber: string): Promise<void> {
      const result = await client.releaseTestCallArm({ path: { voice_agent_id: voiceAgentId }, body: { party_number: partyNumber } });
      bodyOf(result, "releaseTestCallArm");
    },

    async listVoiceAgentLiveDocuments(voiceAgentId: string): Promise<readonly VoiceAgentLiveDocumentView[]> {
      const result = await client.listVoiceAgentLiveDocuments({ path: { voice_agent_id: voiceAgentId } });
      return liveDocumentOf(assertOk(result));
    },

    async endVoiceTestCall(conversationId: string): Promise<TestCallView> {
      const result = await client.endTestCall({ path: { conversation_id: conversationId } });
      return testCallOf(bodyOf(result, "endTestCall"), "endTestCall");
    },

    async sendTestChatTurn(
      voiceAgentId: string,
      text: string,
      languageCode = "",
      agentVersionId?: string,
      surveyArm?: SurveyArmView,
    ): Promise<readonly TestChatTurnView[]> {
      const key = chatKeyOf(voiceAgentId, agentVersionId, languageCode);
      const held = chat.get(key);
      if (held?.ended === true) throw new Error("This conversation has finished. Start a New chat to test again.");
      const previous = attempt.get(key);
      if (previous?.running === true) throw new Error("The agent is still answering. Wait for its reply.");
      if (previous !== undefined && previous.text !== text) {
        throw new Error("Retry the last message first, or choose New chat to start again.");
      }
      const pending = previous ?? { text, requestId: crypto.randomUUID(), running: false };
      pending.running = true;
      attempt.set(key, pending);
      changed();
      const epoch = generation.get(key) ?? 0;
      try {
        const result = await client.createSandboxTurn({
          path: { voice_agent_id: voiceAgentId },
          body: {
            text, request_id: pending.requestId,
            ...(held === undefined ? {} : { conversation_id: held.conversationId }),
            ...(languageCode === "" ? {} : { language_code: languageCode }),
            ...(agentVersionId === undefined ? {} : { agent_version_id: agentVersionId }),
            ...(surveyArm === undefined ? {} : { survey_arm: surveyArm }),
          },
        });
        const produced = sandboxTurnOf(bodyOf(result, "createSandboxTurn"));
        if ((generation.get(key) ?? 0) !== epoch) return chat.get(key)?.turn ?? [];
        const next = [...(held?.turn ?? []), ...produced.turn];
        chat.set(key, { conversationId: produced.conversationId, turn: next, ended: produced.ended });
        attempt.delete(key);
        return next;
      } catch (error) {
        // A definitive preflight refusal did not submit a message. An uncertain failure retains
        // its request id until the same message is retried, including a lost first response.
        if (error instanceof TestCallUnavailableError && error.code === "model_pool_unavailable") {
          if (attempt.get(key) === pending) attempt.delete(key);
        }
        throw error;
      } finally {
        pending.running = false;
        changed();
      }
    },

    async openTestChat(voiceAgentId: string, agentVersionId?: string, surveyArm?: SurveyArmView): Promise<readonly TestChatTurnView[]> {
      // The design draws no language choice for a text test: the opening is in the agent's first language.
      const key = chatKeyOf(voiceAgentId, agentVersionId);
      const held = chat.get(key);
      // A chat that already holds lines is not opened again (a remount, or a second panel on the page).
      if (held !== undefined) return held.turn;
      const epoch = generation.get(key) ?? 0;
      const result = await client.createSandboxTurn({
        path: { voice_agent_id: voiceAgentId },
        body: {
          opening: true,
          request_id: crypto.randomUUID(),
          ...(agentVersionId === undefined ? {} : { agent_version_id: agentVersionId }),
          ...(surveyArm === undefined ? {} : { survey_arm: surveyArm }),
        },
      });
      const produced = sandboxTurnOf(bodyOf(result, "createSandboxTurn"));
      // New chat (or another group) chosen while the opening was on its way: this one is dropped.
      if ((generation.get(key) ?? 0) !== epoch) return chat.get(key)?.turn ?? [];
      const already = chat.get(key);
      if (already !== undefined) return already.turn;
      chat.set(key, { conversationId: produced.conversationId, turn: produced.turn, ended: produced.ended });
      changed();
      return produced.turn;
    },

    testChatSnapshot(voiceAgentId: string, agentVersionId?: string, languageCode = ""): TestChatSnapshot {
      const key = chatKeyOf(voiceAgentId, agentVersionId, languageCode);
      const held = chat.get(key) ?? { turn: [], ended: false };
      const pending = attempt.get(key);
      return { ...held, pending: pending?.running === true ? pending.text : null };
    },

    subscribeTestChat(notify: () => void): () => void {
      listener.add(notify);
      return () => { listener.delete(notify); };
    },

    startNewTestChat(voiceAgentId: string, agentVersionId?: string, languageCode = ""): Promise<void> {
      const key = chatKeyOf(voiceAgentId, agentVersionId, languageCode);
      generation.set(key, (generation.get(key) ?? 0) + 1);
      chat.delete(key);
      attempt.delete(key);
      changed();
      return Promise.resolve();
    },

    async readTestCallAvailability(): Promise<TestCallAvailabilityView> {
      const result = await client.readTestCallAvailability();
      return availabilityOf(bodyOf(result, "readTestCallAvailability"));
    },

    async startBrowserTestCall(voiceAgentId: string, languageCode: string, surveyArm?: SurveyArmView): Promise<BrowserTestCallView> {
      const result = await client.createBrowserTestCall({
        path: { voice_agent_id: voiceAgentId },
        body: {
          language_code: languageCode === "" ? null : languageCode,
          ...(surveyArm === undefined ? {} : { survey_arm: surveyArm }),
        },
      });
      return browserTestCallOf(bodyOf(result, "createBrowserTestCall"));
    },

    async readBrowserTestCall(voiceAgentId: string, conversationId: string): Promise<TestCallView> {
      const result = await client.readTestCall({
        path: { voice_agent_id: voiceAgentId },
        query: { conversation_id: conversationId },
      });
      return testCallOf(bodyOf(result, "readTestCall"), "readTestCall");
    },

    // The one dynamic import of the browser voice module: its own chunk, fetched when a tester
    // chooses "Talk in the browser"; the WebRTC SDK is a further chunk it loads on connect.
    loadBrowserVoiceClient: (): Promise<BrowserVoiceClient> => import("@wetalk/webrtc-client"),
  };
}
